import { api } from './api';
import { toast } from 'sonner';

// Fetch the public Razorpay key from backend (cached in module scope).
let _cfg = null;
async function getConfig() {
    if (_cfg) return _cfg;
    const { data } = await api.get('/billing/config');
    _cfg = data;
    return _cfg;
}

function waitForCheckoutScript(maxMs = 5000) {
    const start = Date.now();
    return new Promise((resolve, reject) => {
        const tick = () => {
            if (typeof window.Razorpay !== 'undefined') return resolve(true);
            if (Date.now() - start > maxMs) return reject(new Error('Razorpay checkout script did not load'));
            setTimeout(tick, 100);
        };
        tick();
    });
}

/**
 * Start a Razorpay checkout flow.
 * @param {Object} opts
 * @param {'plan'|'topup'} opts.type
 * @param {string} [opts.plan_id]
 * @param {string} [opts.topup_id]
 * @param {boolean} [opts.autopay]
 * @param {Function} [opts.onSuccess]  called with verify response
 */
export async function startRazorpayCheckout(opts) {
    const { type, plan_id, topup_id, autopay = false, onSuccess } = opts;
    const cfg = await getConfig();
    if (!cfg.configured || !cfg.key_id) {
        toast.error('Razorpay is not configured yet. Contact support.');
        return;
    }
    await waitForCheckoutScript();

    // 1) Create order/subscription on backend
    let checkout;
    try {
        const { data } = await api.post('/billing/checkout', {
            plan_id: type === 'plan' ? plan_id : undefined,
            topup_id: type === 'topup' ? topup_id : undefined,
            autopay,
            origin_url: window.location.origin,
        });
        checkout = data;
    } catch (e) {
        toast.error(e.response?.data?.detail || 'Checkout could not be initiated');
        return;
    }

    const isSubscription = checkout.mode === 'subscription';
    const options = {
        key: cfg.key_id,
        name: 'Magnidrop',
        description: isSubscription
            ? 'Auto-renewal enabled — UPI Autopay / e-mandate'
            : 'One-time payment — manual renewal via email',
        image: '/logo192.png',
        theme: { color: '#FF6B35' },
        prefill: checkout.prefill || {},
        handler: async (response) => {
            try {
                const verifyBody = {
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    kind: isSubscription ? 'subscription' : (type === 'topup' ? 'topup' : 'one_time'),
                };
                if (response.razorpay_order_id) verifyBody.razorpay_order_id = response.razorpay_order_id;
                if (response.razorpay_subscription_id) verifyBody.razorpay_subscription_id = response.razorpay_subscription_id;
                if (isSubscription && !verifyBody.razorpay_subscription_id) {
                    verifyBody.razorpay_subscription_id = checkout.subscription_id;
                }
                if (!isSubscription && !verifyBody.razorpay_order_id) {
                    verifyBody.razorpay_order_id = checkout.order_id;
                }
                const { data } = await api.post('/billing/verify', verifyBody);
                toast.success(isSubscription ? 'Auto-renewal activated — you\'re all set.' : 'Payment successful — plan activated.');
                if (onSuccess) onSuccess(data);
                else window.location.href = '/billing/success?tx=' + data.tx_id;
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Payment verification failed. Please contact support.');
            }
        },
        modal: {
            ondismiss: () => {
                toast.message('Checkout closed. No charge was made.');
            },
        },
    };
    if (isSubscription) {
        options.subscription_id = checkout.subscription_id;
    } else {
        options.order_id = checkout.order_id;
        options.amount = checkout.amount;
        options.currency = checkout.currency || 'INR';
    }

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed. Please try again.');
    });
    rzp.open();
}


/**
 * Flat $15 (₹ equivalent) Beta Pass checkout. Different endpoints from the
 * retail flow so beta payment.captured events can be routed via
 * /api/beta/webhook/razorpay to flip `is_paid_beta=true` in Supabase.
 */
export async function startBetaCheckout({ onSuccess } = {}) {
    const cfg = await getConfig();
    if (!cfg.configured || !cfg.key_id) {
        toast.error('Razorpay is not configured yet. Contact support.');
        return;
    }
    await waitForCheckoutScript();

    let checkout;
    try {
        const { data } = await api.post('/beta/checkout', {});
        checkout = data;
    } catch (e) {
        const detail = e.response?.data?.detail;
        if (typeof detail === 'object' && detail?.error === 'BETA_FULL') {
            toast.error(detail.message || 'Beta is full — official launch coming soon.');
            return;
        }
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Beta checkout unavailable'));
        return;
    }

    const options = {
        key: cfg.key_id,
        name: 'Magnidrop Paid Beta',
        description: `Founding Tester Pass — flat $${checkout.beta_price_usd_display || 15}`,
        image: '/logo192.png',
        theme: { color: '#FF6B35' },
        prefill: checkout.prefill || {},
        order_id: checkout.order_id,
        amount: checkout.amount,
        currency: checkout.currency || 'INR',
        handler: async (response) => {
            try {
                const { data } = await api.post('/beta/verify', {
                    razorpay_order_id: response.razorpay_order_id || checkout.order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                });
                toast.success('Welcome, founding tester! 🚀');
                if (onSuccess) onSuccess(data);
                else window.location.href = '/dashboard';
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Payment verification failed. Please contact support.');
            }
        },
        modal: {
            ondismiss: () => toast.message('Checkout closed. No charge was made.'),
        },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed. Please try again.');
    });
    rzp.open();
}
